import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Text,
  Paper,
  Title,
  SimpleGrid,
  Avatar,
  Flex,
  Box,
  NumberFormatter,
  Anchor,
  Button,
  VisuallyHidden,
} from "@mantine/core";
import {
  BanknotesIcon,
  BriefcaseIcon,
  UserGroupIcon,
  ArrowRightCircleIcon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/solid";
import InsurancesListItem from "../components/insurancesListItem";
import BankIDLogo from "../assets/bank-id-logo.svg?react";
import { useGetAdviceById } from "../hooks/api";
import type { AdviceFund } from "../hooks/api/useGetAdviceById";
import { useGetLockedAdvice } from "../hooks/api/useGetLockedAdvice";
import { useUpdateCustomerData } from "../hooks/ui";
import { isFullCredentials } from "../hooks/ui/customerDataStore";
import ErrorComponent from "../components/errorComponent";
import { LoaderComponent } from "../components/loaderComponent";
import { FooterButtons } from "../components/footerButtons";
import { isMobileDevice } from "../utils/useDeviceDetection";
import PoaExpiredBanner from "../components/poaValidation";

const isMobile = isMobileDevice();

export const Route = createFileRoute("/transfer-terms")({
  component: TransferTermsPage,
});

function TransferTermsPage() {
  const { customerData } = useUpdateCustomerData();
  const {
    data: advice,
    isLoading,
    isError,
  } = useGetAdviceById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });
  const {
    data: lockedAdviceData,
    isLoading: isLockedAdviceLoading,
    isError: isLockedAdviceError,
  } = useGetLockedAdvice({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });
  const isLocked = !!lockedAdviceData?.isLocked;

  const toFunds = advice?.insuranceAdvices.map(
    (insurance) => JSON.parse(insurance.toFunds as string) as AdviceFund[],
  );

  if (isLockedAdviceLoading || isLoading) {
    return <LoaderComponent />;
  }

  if (isLockedAdviceError || isError) {
    return <ErrorComponent />;
  }

  if (!advice?.insuranceAdvices.length || !isLocked) {
    return (
      <>
        <PoaExpiredBanner />
        <Flex direction="column" justify="center" mih="40vh" px="lg">
          <Title ff={"var(--wallet-font-family-sambla-sans)"}>
            Du saknar ett giltigt råd. Din rådgivare måste generera ett råd
            innan du går vidare.
          </Title>
        </Flex>
        <FooterButtons>
          <Button
            component={Link}
            to="/advice-summary"
            fullWidth
            variant="default"
          >
            Tillbaka
          </Button>
        </FooterButtons>
      </>
    );
  }

  return (
    <>
      <Title ff={"var(--wallet-font-family-sambla-sans)"} mt="xl" mb="sm">
        Öppna nya pensions{"\u00AD"}försäkringar hos Idun Liv och låt Sambla
        rådge dig
      </Title>
      <Text mb={"xl"}>
        Vi kommer att öppna nya försäkringar hos Idun Liv dit vi flyttar vissa
        av dina nuvarande försäkringar. Vänligen läs igenom villkoren innan du
        går vidare.
      </Text>

      <Paper radius={"lg"} mb={"xl"}>
        <Title
          ff={"var(--wallet-font-family-sambla-sans)"}
          order={2}
          size="h3"
          mb={"xs"}
          pt="xl"
          pl="xl"
        >
          Villkor för flytt till Idun Liv
        </Title>
        <SimpleGrid
          cols={{ base: 1 }}
          verticalSpacing="lg"
          spacing={"xl"}
          p={"xl"}
        >
          <Flex direction={isMobile ? "column" : "row"}>
            <Avatar mr={isMobile ? 0 : "lg"} mb={isMobile ? "sm" : 0}>
              <BriefcaseIcon
                height="24px"
                color={"var(--wallet-color-sambla-green-shade-700)"}
              />
            </Avatar>
            <Box>
              <Title order={3} size="h5">
                Pensionsålder
              </Title>
              <Text c="dimmed" size="sm">
                Vi kommer att behålla samma pensionsålder som du har på dina
                försäkringar idag. Du kommer att kunna justera detta när du
                börjar närma dig pensionen.
              </Text>
            </Box>
          </Flex>
          <Flex direction={isMobile ? "column" : "row"}>
            <Avatar mr={isMobile ? 0 : "lg"} mb={isMobile ? "sm" : 0}>
              <UserGroupIcon
                height="24px"
                color={"var(--wallet-color-sambla-green-shade-700)"}
              />
            </Avatar>
            <Box>
              <Title order={3} size="h5">
                Efterlevandeskydd
              </Title>
              <Text c="dimmed" size="sm">
                Alla försäkringar som flyttas till Idun Liv kommer att ha
                efterlevandeskydd. Detta gäller även de försäkringar som saknar
                efterlevandeskydd idag.
              </Text>
            </Box>
          </Flex>
          <Flex direction={isMobile ? "column" : "row"}>
            <Avatar mr={isMobile ? 0 : "lg"} mb={isMobile ? "sm" : 0}>
              <ArrowRightCircleIcon
                height="24px"
                color={"var(--wallet-color-sambla-green-shade-700)"}
              />
            </Avatar>
            <Box>
              <Title order={3} size="h5">
                Utbetalning
              </Title>
              <Text c="dimmed" size="sm">
                Vi kommer sätta upp en utbetalningsplan tillsammans med dig när
                du börjar närma dig din pension och justera utbetalningstiden
                efter dina behov. Fram tills dess kommer vi att behålla samma
                pensionsålder och utbetalningstid som du har på dina
                försäkringar idag med undantag för livsvariga pensioner som vi
                istället sätter till en utbetalningsperiod på 30 år
              </Text>
            </Box>
          </Flex>
          <Flex direction={isMobile ? "column" : "row"}>
            <Avatar mr={isMobile ? 0 : "lg"} mb={isMobile ? "sm" : 0}>
              <BanknotesIcon
                height="24px"
                color={"var(--wallet-color-sambla-green-shade-700)"}
              />
            </Avatar>
            <Box>
              <Title order={3} size="h5">
                Avgift
              </Title>
              <Text c="dimmed" size="sm">
                Du betalar aldrig mer än 0,87%. Detta inkluderar 0,50% för
                Samblas rådgivning, 0.25% för försäkringen hos Idun Liv, samt
                fondavgifter på 0,10-0,12% som går oavkortat till
                fondförvaltarna.
              </Text>
            </Box>
          </Flex>
          <Flex direction={isMobile ? "column" : "row"}>
            <Avatar mr={isMobile ? 0 : "lg"} mb={isMobile ? "sm" : 0}>
              <ChartBarIcon
                height="24px"
                color={"var(--wallet-color-sambla-green-shade-700)"}
              />
            </Avatar>
            <Box>
              <Title order={3} size="h5">
                Allokering
              </Title>
              {/* <Text c="dimmed" size="sm" mb={"lg"}>
                Vi kommer att flytta pension till ett värde av SUM_OF_ASSETS SEK
                och allokerar enligt följande:
              </Text> */}
              <Text c="dimmed" size="sm" mb={"lg"}>
                Det flyttade kapitalet kommer investeras enligt följande
                fördelning:
              </Text>
              {toFunds?.[0]?.map((asset) => {
                return (
                  <Flex
                    key={asset.isin || asset.name}
                    justify={"space-between"}
                    mb="md"
                  >
                    <Box>
                      <Title order={4} size="h6" mb="xs">
                        {asset.name}
                      </Title>
                      <Flex align={"center"}>
                        <Anchor
                          c="dimmed"
                          mr="xs"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${import.meta.env.VITE_MORNINGSTAR_URL}&documenttype=1&language=${import.meta.env.VITE_MORNINGSTAR_LANGUAGE}&isin=${asset.isin}`}
                        >
                          <Text size="xs">
                            Faktablad{" "}
                            <ArrowTopRightOnSquareIcon
                              height={"11px"}
                              width={"11px"}
                            />
                            <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>
                          </Text>
                        </Anchor>
                        <Anchor
                          c="dimmed"
                          mr="xs"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${import.meta.env.VITE_MORNINGSTAR_URL}&documenttype=299&language=${import.meta.env.VITE_MORNINGSTAR_LANGUAGE}&isin=${asset.isin}`}
                        >
                          <Text size="xs">
                            Hållbarhet{" "}
                            <ArrowTopRightOnSquareIcon
                              height={"11px"}
                              width={"11px"}
                            />{" "}
                            <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>
                          </Text>
                        </Anchor>
                      </Flex>
                    </Box>
                    <Title order={4} size="h6" mb="xs">
                      <NumberFormatter
                        value={asset.shareOfTotal}
                        decimalScale={2}
                        suffix="%"
                      />
                    </Title>
                  </Flex>
                );
              })}
            </Box>
          </Flex>
        </SimpleGrid>
        <Paper
          bg={"var(--wallet-color-neutrals-gray-100)"}
          style={{ borderTopLeftRadius: "0", borderTopRightRadius: "0" }}
          p={"lg"}
        >
          <Text c="dimmed" ta={"center"} fz={"12px"}>
            För fullständiga villkor vänligen se &nbsp;
            <Anchor
              href="/documents/TF003 Faktablad Tjänstepension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Faktablad Tjänstepension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/TF004 Förköpsinformation Tjänstepension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Förköpsinformation Tjänstepension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/TF006 Produktvillkor Tjänstepension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Produktvillkor Tjänstepension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/TF011 Målmarknad Tjänstepension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Målmarknad Tjänstepension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/PF003 Faktablad Privatpension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Faktablad Privatpension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/PF004 Förköpsinformation Privatpension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Förköpsinformation Privatpension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/PF006 Produktvillkor Privatpension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Produktvillkor Privatpension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/PF008 Målmarknad Privatpension Fond.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Målmarknad Privatpension,
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>&nbsp;
            </Anchor>
            <Anchor
              href="/documents/IL001 Allmänna_försäkringsvillkor.pdf"
              underline="always"
              c="dimmed"
              target="_blank"
              rel="noopener noreferrer"
              fz={"12px"}
            >
              Allmänna Försäkringsvillkor.
              <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>
            </Anchor>
          </Text>
        </Paper>
      </Paper>

      <Paper p={"xl"} radius={"lg"} mb={"xl"}>
        <Title ff={"var(--wallet-font-family-sambla-sans)"} order={3} mb={"md"}>
          Dessa försäkringarna flyttas till nya försäkringar hos Idun Liv
        </Title>

        {advice.insuranceAdvices?.map((insurance, index) => (
          <InsurancesListItem
            insurance={insurance}
            key={`${insurance.insuranceIdentifier}-${index}`}
          />
        ))}
      </Paper>

      <FooterButtons>
        <Button component={Link} to="/advice-summary" variant="default">
          Tillbaka
        </Button>

        <Button component={Link} to="/open-sign-policy">
          Skriv under
          <Box ml={"sm"}>
            <BankIDLogo role="img" aria-label="BankID" />
          </Box>
        </Button>
      </FooterButtons>
    </>
  );
}
